import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Stack } from '@mui/system';
import { Divider } from '@mui/material';
import { v4 as uuid } from 'uuid';
import { Areas, JustInTimeRoutingConfig } from '@thive/fddm-types';

import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { JitRoutingPoint } from './JitRoutingPoint';
import { validTargetAreas, validWaitAreas } from '../constants';
import { useJitRoutingPoints } from '../hooks/useJitRoutingPoints';
import { areasToFlatCollection } from '@/modules/floorplanDesign/helpers/helpers';
import { isSingularStationArea, isStationaryArea } from '@/modules/floorplanDesign/helpers';
import { FddmServiceFactory as FloorplanDesignServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { getHighestJitRoutingPointIndex } from '../helpers';

type AreaItem = { value: string; label: string };

interface Props {
  areas: Areas;
  relatedJitRoutingPoints: JustInTimeRoutingConfig[];
  jitRoutingPoints: JustInTimeRoutingConfig[];
  showDraft: boolean;
  segmentId: string;
  onDismissDraft: () => void;
}

function JitRoutingPointsComponent({
  areas,
  relatedJitRoutingPoints,
  jitRoutingPoints,
  showDraft,
  segmentId,
  onDismissDraft,
}: Props) {
  const fddmSvc = useMemo(
    () => FloorplanDesignServiceFactory.getInstance().createFddmService(),
    [],
  );
  const { getJitRoutingConfigs } = useFloorplanDesignStore();
  const {
    addTargetToJitRoutingPoint,
    removeFromJitRoutingPoint,
    removeJitRoutingPoint,
    addWaitAreaToJitRoutingPoint,
    setRelativePositionForJitRoutingPoint,
    getValidSiblingTargets,
  } = useJitRoutingPoints();

  const [draftJitRoutingPointId, setDraftJitRoutingPointId] = useState<string | null>(null);

  // Reset the draft slot whenever the panel closes/reopens it.
  useEffect(() => {
    // plz fix me
    // eslint-disable-next-line react-hooks/set-state-in-effect
    if (!showDraft) setDraftJitRoutingPointId(null);
  }, [showDraft]);

  const allAreas = useMemo(() => areasToFlatCollection(areas), [areas]);
  const allAreaItems = useMemo<AreaItem[]>(
    () => allAreas.map((area) => ({ value: area.id, label: area.name })),
    [allAreas],
  );

  const usedTargetAreaIds = useMemo(
    () => new Set(relatedJitRoutingPoints.flatMap((point) => point.targetAreaIds)),
    [relatedJitRoutingPoints],
  );
  const usedWaitAreaIds = useMemo(
    () => new Set(relatedJitRoutingPoints.flatMap((point) => point.waitAreaIds)),
    [relatedJitRoutingPoints],
  );

  const draftJitRoutingPoint = useMemo(
    () => jitRoutingPoints.find((point) => point.id === draftJitRoutingPointId) ?? null,
    [draftJitRoutingPointId, jitRoutingPoints],
  );

  // The draft slot owns its committed routing point until graduation; hide
  // it from the regular list so it isn't rendered twice.
  const visibleJitRoutingPoints = useMemo(
    () =>
      draftJitRoutingPointId
        ? relatedJitRoutingPoints.filter((point) => point.id !== draftJitRoutingPointId)
        : relatedJitRoutingPoints,
    [relatedJitRoutingPoints, draftJitRoutingPointId],
  );

  const selectableTargetAreas = useMemo<AreaItem[]>(
    () =>
      allAreas
        .filter(
          (area) =>
            !usedTargetAreaIds.has(area.id) &&
            isSingularStationArea(area) &&
            validTargetAreas.includes(area.loadHandlingType),
        )
        .map((area) => ({ value: area.id, label: area.name })),
    [allAreas, usedTargetAreaIds],
  );

  const selectableWaitAreas = useMemo<AreaItem[]>(
    () =>
      allAreas
        .filter(
          (area) =>
            !usedWaitAreaIds.has(area.id) &&
            isStationaryArea(area) &&
            validWaitAreas.includes(area.stationaryType),
        )
        .map((area) => ({ value: area.id, label: area.name })),
    [allAreas, usedWaitAreaIds],
  );

  const handleSelectTarget = useCallback(
    (targetId: string, jitRoutingPointId: string, oldValue?: string) => {
      addTargetToJitRoutingPoint(targetId, jitRoutingPointId, oldValue);
    },
    [addTargetToJitRoutingPoint],
  );

  const handleRemoveTarget = useCallback(
    (targetId: string, jitRoutingPointId: string) => {
      removeFromJitRoutingPoint([targetId], jitRoutingPointId);
    },
    [removeFromJitRoutingPoint],
  );

  const handleSelectWaitArea = useCallback(
    (waitAreaId: string, jitRoutingPointId: string, oldValue?: string) => {
      addWaitAreaToJitRoutingPoint(waitAreaId, jitRoutingPointId, oldValue);
    },
    [addWaitAreaToJitRoutingPoint],
  );

  const handleRemoveWaitArea = useCallback(
    (waitAreaId: string, jitRoutingPointId: string) => {
      removeFromJitRoutingPoint([waitAreaId], jitRoutingPointId);
    },
    [removeFromJitRoutingPoint],
  );

  const handleRemoveJitRoutingPoint = useCallback(
    (jitRoutingPointId: string) => {
      removeJitRoutingPoint(jitRoutingPointId);
      if (jitRoutingPointId === draftJitRoutingPointId) onDismissDraft();
    },
    [removeJitRoutingPoint, draftJitRoutingPointId, onDismissDraft],
  );

  const handleSetRelativePosition = useCallback(
    (value: number, jitRoutingPointId: string) => {
      setRelativePositionForJitRoutingPoint(value, jitRoutingPointId);
    },
    [setRelativePositionForJitRoutingPoint],
  );

  const handleCreateFromDraft = useCallback(
    (targetId: string) => {
      const validSiblingShapeIds = getValidSiblingTargets(targetId);
      const targetAreaIds: [string, ...string[]] = [
        targetId,
        ...validSiblingShapeIds.filter((id) => id !== targetId),
      ];
      const nextIndex = getHighestJitRoutingPointIndex(getJitRoutingConfigs()) + 1;

      const newConfig: JustInTimeRoutingConfig = {
        id: uuid(),
        name: `JIT routing point ${nextIndex}`,
        highwayAreaId: segmentId,
        targetAreaIds,
        waitAreaIds: [],
        relativePositionOnHighwayInPerc: 0,
      };

      // Stake the draft id before the store update — any interim render
      // must still hide the new routing point from the regular list,
      // otherwise a second `<JitRoutingPoint>` mounts and its auto-start
      // picker cancels the draft slot's active picker.
      setDraftJitRoutingPointId(newConfig.id);
      fddmSvc.addJitRoutingConfigs([newConfig]);
    },
    [fddmSvc, getJitRoutingConfigs, getValidSiblingTargets, segmentId],
  );

  return (
    <Stack spacing={2} divider={<Divider />}>
      {visibleJitRoutingPoints.map((jitRoutingPoint) => (
        <JitRoutingPoint
          key={jitRoutingPoint.id}
          jitRoutingPoint={jitRoutingPoint}
          allAreaItems={allAreaItems}
          waitAreaItems={selectableWaitAreas}
          targetAreaItems={selectableTargetAreas}
          onSelectTarget={handleSelectTarget}
          onRemoveTarget={handleRemoveTarget}
          onRemoveJitRoutingPoint={handleRemoveJitRoutingPoint}
          onSelectWaitArea={handleSelectWaitArea}
          onRemoveWaitArea={handleRemoveWaitArea}
          onSetRelativePosition={handleSetRelativePosition}
        />
      ))}
      {showDraft && (
        <JitRoutingPoint
          key='draft'
          jitRoutingPoint={draftJitRoutingPoint}
          allAreaItems={allAreaItems}
          waitAreaItems={selectableWaitAreas}
          targetAreaItems={selectableTargetAreas}
          onSelectTarget={handleSelectTarget}
          onRemoveTarget={handleRemoveTarget}
          onRemoveJitRoutingPoint={handleRemoveJitRoutingPoint}
          onSelectWaitArea={handleSelectWaitArea}
          onRemoveWaitArea={handleRemoveWaitArea}
          onSetRelativePosition={handleSetRelativePosition}
          onCreate={handleCreateFromDraft}
          onDismissDraft={onDismissDraft}
        />
      )}
    </Stack>
  );
}

export const JitRoutingPoints = memo(JitRoutingPointsComponent);
